/* The eager half of `/library/search`, the one Search page: moments inside recordings. See
 * `dataset-search.ts` for why a route's search vocabulary lives apart from the screen
 * that reads it. */

import { integerParam, stringParam, trimmedStringParam } from "../../../lib/url-state";

/** The frame a search-by-example is asking about: one moment of one entity in one segment
 *  of one dataset. Four values, so four keys — see `LibrarySemanticState`. */
interface FrameExample {
  readonly dataset: string;
  readonly segment: string;
  readonly path: string;
  readonly t: number;
}

/** What `/library/search` is searching for: the words in its one box, or the frame whose
 *  picture replaces them.
 *
 * A frame example is four named scalars rather than one JSON object, because a structured
 * value in a URL is a schema nobody can read, type by hand, or parse without re-deriving
 * it at the boundary. Absent means absent; the four move together, and `frameExample`
 * below is the one place that says so. */
/** How far back Search looks, as a person says it. Absent is any time. */
export const SEARCH_WHEN = ["day", "week", "month"] as const;
export type SearchWhen = (typeof SEARCH_WHEN)[number];

export const SEARCH_WHEN_LABEL: Readonly<Record<SearchWhen | "any", string>> = {
  any: "Any time",
  day: "Today",
  week: "7 days",
  month: "30 days",
};

/** The first moment a window keeps: the start of today where the reader lives, or seven or
 *  thirty days before now. */
export function searchSince(when: SearchWhen | undefined, now = new Date()): Date | undefined {
  if (when === undefined) return undefined;
  if (when === "day") return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return new Date(now.getTime() - (when === "week" ? 7 : 30) * 24 * 60 * 60 * 1000);
}

export interface LibrarySemanticState {
  readonly q?: string;
  /** How far back to look, for the latest episodes and for a search alike. */
  readonly when?: SearchWhen;
  readonly exampleDataset?: string;
  readonly exampleSegment?: string;
  readonly examplePath?: string;
  readonly exampleT?: number;
}

/** The frame being searched by, when all four keys name one — parsed once, here. */
export function frameExample(state: LibrarySemanticState): FrameExample | undefined {
  const { exampleDataset, exampleSegment, examplePath, exampleT } = state;
  return exampleDataset === undefined ||
    exampleSegment === undefined ||
    examplePath === undefined ||
    exampleT === undefined
    ? undefined
    : {
        dataset: exampleDataset,
        segment: exampleSegment,
        path: examplePath,
        t: exampleT,
      };
}

/** Stop searching by a frame: all four keys leave together or the example is half a
 *  frame, which is not one. */
export const NO_FRAME: Partial<LibrarySemanticState> = {
  exampleDataset: undefined,
  exampleSegment: undefined,
  examplePath: undefined,
  exampleT: undefined,
};

export function parseSemanticSearch(search: Record<string, unknown>): LibrarySemanticState {
  return {
    q: stringParam(search.q),
    when: SEARCH_WHEN.find((when) => when === search.when),
    exampleDataset: trimmedStringParam(search.exampleDataset),
    exampleSegment: trimmedStringParam(search.exampleSegment),
    examplePath: trimmedStringParam(search.examplePath),
    exampleT: integerParam(search.exampleT, { min: 0 }),
  };
}
